import { useState, useEffect, useCallback } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Timeslot {
  start_time: string; // "YYYY-MM-DD HH:mm:ss"
  end_time: string;
}

interface BookingTexts {
  stepDateTitle: string;
  stepTimeTitle: string;
  stepFormTitle: string;
  prevMonth: string;
  nextMonth: string;
  noSlots: string;
  labelName: string;
  labelPhone: string;
  labelNotes: string;
  phName: string;
  phPhone: string;
  phNotes: string;
  selectedDate: string;
  selectedTime: string;
  modifyTime: string;
  submitBtn: string;
  submitting: string;
  successTitle: string;
  successDesc: string;
  errorMsg: string;
  backBtn: string;
  loadingSlots: string;
  required: string;
}

interface BookingCalendarProps {
  timeslotsApi: string;
  appointmentsApi: string;
  texts: BookingTexts;
  containerStyle?: React.CSSProperties;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DAYS_ES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MONTHS_ES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

function extractDate(dt: string): string {
  return dt.split(' ')[0];
}

function extractTime(dt: string): string {
  const parts = dt.split(' ');
  if (parts.length < 2) return '';
  return parts[1].substring(0, 5);
}

function todayStr(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getMonthYear(dateStr: string): { year: number; month: number } {
  const [y, m] = dateStr.split('-');
  return { year: parseInt(y, 10), month: parseInt(m, 10) - 1 };
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function firstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

function formatMonthLabel(year: number, month: number): string {
  return `${MONTHS_ES[month]} ${year}`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface CalendarGridProps {
  year: number;
  month: number;
  availableDates: Set<string>;
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
  onPrev: () => void;
  onNext: () => void;
  texts: BookingTexts;
}

function CalendarGrid({
  year, month, availableDates, selectedDate, onSelectDate, onPrev, onNext, texts,
}: CalendarGridProps) {
  const totalDays = daysInMonth(year, month);
  const firstDay = firstDayOfMonth(year, month);
  const today = todayStr();

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(d);

  const rows: (number | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    rows.push(cells.slice(i, i + 7));
  }

  return (
    <div className="w-full">
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={onPrev}
          className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100 transition cursor-pointer"
          aria-label={texts.prevMonth}
        >
          <i className="ri-arrow-left-s-line text-slate-600 text-lg"></i>
        </button>
        <span className="text-slate-800 font-semibold text-sm">
          {formatMonthLabel(year, month)}
        </span>
        <button
          type="button"
          onClick={onNext}
          className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100 transition cursor-pointer"
          aria-label={texts.nextMonth}
        >
          <i className="ri-arrow-right-s-line text-slate-600 text-lg"></i>
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 mb-1">
        {DAYS_ES.map((d) => (
          <div key={d} className="text-center text-xs font-semibold text-slate-400 py-1">
            {d}
          </div>
        ))}
      </div>

      {/* Day cells */}
      {rows.map((row, ri) => (
        <div key={ri} className="grid grid-cols-7">
          {row.map((day, ci) => {
            if (!day) {
              return <div key={`e-${ci}`} className="h-10" />;
            }
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isAvailable = availableDates.has(dateStr);
            const isSelected = selectedDate === dateStr;
            const isPast = dateStr < today;

            let cellClass =
              'h-10 flex flex-col items-center justify-center rounded-lg text-xs font-medium transition-all ';

            if (isSelected) {
              cellClass += 'bg-[#0F2340] text-white cursor-pointer';
            } else if (isAvailable && !isPast) {
              cellClass +=
                'bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100 cursor-pointer';
            } else {
              cellClass += 'text-slate-300 cursor-not-allowed';
            }

            return (
              <div key={dateStr} className="p-0.5">
                <button
                  type="button"
                  disabled={!isAvailable || isPast}
                  onClick={() => isAvailable && !isPast && onSelectDate(dateStr)}
                  className={`w-full ${cellClass}`}
                >
                  <span>{day}</span>
                  {isAvailable && !isPast && !isSelected && (
                    <span className="w-1 h-1 rounded-full bg-sky-400 mt-0.5"></span>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function BookingCalendar({ timeslotsApi, appointmentsApi, texts, containerStyle }: BookingCalendarProps) {
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [timeslots, setTimeslots] = useState<Timeslot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [calYear, setCalYear] = useState<number>(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState<number>(new Date().getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Timeslot | null>(null);
  const [formName, setFormName] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const fetchSlots = useCallback(async () => {
    setLoadingSlots(true);
    try {
      const res = await fetch(timeslotsApi);
      const data: Timeslot[] = await res.json();
      setTimeslots(data);

      // Auto-navigate to closest available date
      const today = todayStr();
      const futureDates = data
        .map((s) => extractDate(s.start_time))
        .filter((d) => d >= today)
        .sort();

      if (futureDates.length > 0) {
        const closest = futureDates[0];
        const { year, month } = getMonthYear(closest);
        setCalYear(year);
        setCalMonth(month);
        setSelectedDate(closest);
      } else if (data.length > 0) {
        const latest = data
          .map((s) => extractDate(s.start_time))
          .sort()
          .reverse()[0];
        const { year, month } = getMonthYear(latest);
        setCalYear(year);
        setCalMonth(month);
      }
    } catch {
      // silently fail
    } finally {
      setLoadingSlots(false);
    }
  }, [timeslotsApi]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  const availableDates = new Set(timeslots.map((s) => extractDate(s.start_time)));

  const slotsForDate = selectedDate
    ? timeslots.filter((s) => extractDate(s.start_time) === selectedDate)
    : [];

  const handleSelectDate = (date: string) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setStep(0);
  };

  const handlePrevMonth = () => {
    if (calMonth === 0) {
      setCalMonth(11);
      setCalYear((y) => y - 1);
    } else {
      setCalMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (calMonth === 11) {
      setCalMonth(0);
      setCalYear((y) => y + 1);
    } else {
      setCalMonth((m) => m + 1);
    }
  };

  const handleSelectSlot = (slot: Timeslot) => {
    setSelectedSlot(slot);
    setStep(2);
  };

  const handleModifyTime = () => {
    setStep(1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSlot) return;
    setSubmitting(true);
    setSubmitError(false);

    const payload = {
      customer_name: formName,
      customer_phone: formPhone,
      start_time: selectedSlot.start_time,
      end_time: selectedSlot.end_time,
      notes: formNotes,
    };

    try {
      const res = await fetch(appointmentsApi, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setSubmitted(true);
        await fetchSlots();
        setSelectedDate(null);
        setSelectedSlot(null);
        setFormName('');
        setFormPhone('');
        setFormNotes('');
        setStep(0);
      } else {
        setSubmitError(true);
      }
    } catch {
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Step indicator ──────────────────────────────────────────────────────────
  const StepIndicator = () => (
    <div className="flex items-center gap-2 mb-6">
      {[0, 1, 2].map((s) => (
        <div key={s} className="flex items-center gap-2">
          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
              step === s
                ? 'bg-[#0F2340] text-white'
                : step > s
                ? 'bg-sky-500 text-white'
                : 'bg-slate-100 text-slate-400'
            }`}
          >
            {step > s ? <i className="ri-check-line text-xs"></i> : s + 1}
          </div>
          {s < 2 && (
            <div
              className={`h-0.5 w-8 rounded-full transition-all ${
                step > s ? 'bg-sky-500' : 'bg-slate-200'
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );

  // ── Success screen ──────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div
        style={containerStyle}
        className="bg-white rounded-2xl p-8 flex flex-col items-center justify-center text-center min-h-[400px] gap-4"
      >
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
          <i className="ri-calendar-check-line text-emerald-600 text-3xl"></i>
        </div>
        <h3 className="text-xl font-bold text-slate-800">{texts.successTitle}</h3>
        <p className="text-slate-500 text-sm max-w-xs leading-relaxed">{texts.successDesc}</p>
        <button
          type="button"
          onClick={() => setSubmitted(false)}
          className="mt-2 px-6 py-2.5 bg-[#0F2340] text-white text-sm font-semibold rounded-lg hover:bg-sky-700 transition cursor-pointer whitespace-nowrap"
        >
          {texts.backBtn}
        </button>
      </div>
    );
  }

  return (
    <div style={containerStyle} className="bg-white rounded-2xl p-6 md:p-8">
      <StepIndicator />

      {/* ── STEP 0: Select Date ─────────────────────────────────────────────── */}
      {step === 0 && (
        <div>
          <h3 className="text-lg font-bold text-slate-800 mb-4">{texts.stepDateTitle}</h3>
          {loadingSlots ? (
            <div className="flex items-center justify-center py-12 text-slate-400 text-sm gap-2">
              <i className="ri-loader-4-line animate-spin text-lg"></i>
              {texts.loadingSlots}
            </div>
          ) : (
            <>
              <CalendarGrid
                year={calYear}
                month={calMonth}
                availableDates={availableDates}
                selectedDate={selectedDate}
                onSelectDate={handleSelectDate}
                onPrev={handlePrevMonth}
                onNext={handleNextMonth}
                texts={texts}
              />
              {selectedDate && (
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="mt-5 w-full py-3 bg-[#0F2340] text-white text-sm font-bold rounded-lg hover:bg-sky-700 transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-time-line"></i>
                  {texts.stepTimeTitle}
                  <i className="ri-arrow-right-line"></i>
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* ── STEP 1: Select Time Slot ────────────────────────────────────────── */}
      {step === 1 && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <button
              type="button"
              onClick={() => setStep(0)}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition cursor-pointer"
            >
              <i className="ri-arrow-left-line text-slate-600"></i>
            </button>
            <h3 className="text-lg font-bold text-slate-800">{texts.stepTimeTitle}</h3>
          </div>

          {selectedDate && (
            <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-sky-50 rounded-lg border border-sky-100">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-line text-sky-500 text-sm"></i>
              </div>
              <span className="text-sky-700 text-sm font-medium">{selectedDate}</span>
            </div>
          )}

          {slotsForDate.length === 0 ? (
            <p className="text-slate-400 text-sm py-6 text-center">{texts.noSlots}</p>
          ) : (
            <div className="grid grid-cols-2 gap-2.5">
              {slotsForDate.map((slot) => {
                const start = extractTime(slot.start_time);
                const end = extractTime(slot.end_time);
                const isChosen =
                  selectedSlot?.start_time === slot.start_time;
                return (
                  <button
                    key={slot.start_time}
                    type="button"
                    onClick={() => handleSelectSlot(slot)}
                    className={`py-3 px-4 rounded-lg border text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                      isChosen
                        ? 'bg-[#0F2340] text-white border-[#0F2340]'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-sky-400 hover:bg-sky-50'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-1.5">
                      <i className={`ri-time-line text-xs ${isChosen ? 'text-sky-300' : 'text-sky-500'}`}></i>
                      {start} – {end}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── STEP 2: Review + Form + Submit ──────────────────────────────────── */}
      {step === 2 && selectedSlot && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition cursor-pointer"
            >
              <i className="ri-arrow-left-line text-slate-600"></i>
            </button>
            <h3 className="text-lg font-bold text-slate-800">{texts.stepFormTitle}</h3>
          </div>

          {/* Read-only booking review */}
          <div className="bg-slate-50 rounded-xl p-4 mb-5 border border-slate-100">
            <div className="flex items-start justify-between gap-3">
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-check-line text-sky-500 text-sm"></i>
                  </div>
                  <span className="text-slate-500 text-xs">{texts.selectedDate}</span>
                  <span className="text-slate-800 text-xs font-semibold">{extractDate(selectedSlot.start_time)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line text-sky-500 text-sm"></i>
                  </div>
                  <span className="text-slate-500 text-xs">{texts.selectedTime}</span>
                  <span className="text-slate-800 text-xs font-semibold">
                    {extractTime(selectedSlot.start_time)} – {extractTime(selectedSlot.end_time)}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={handleModifyTime}
                className="text-xs text-sky-600 hover:text-sky-800 font-semibold underline cursor-pointer whitespace-nowrap shrink-0"
              >
                {texts.modifyTime}
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                {texts.labelName} <span className="text-red-400">{texts.required}</span>
              </label>
              <input
                type="text"
                required
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder={texts.phName}
                className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                {texts.labelPhone} <span className="text-red-400">{texts.required}</span>
              </label>
              <input
                type="tel"
                required
                value={formPhone}
                onChange={(e) => setFormPhone(e.target.value)}
                placeholder={texts.phPhone}
                className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                {texts.labelNotes}
              </label>
              <textarea
                rows={3}
                value={formNotes}
                onChange={(e) => setFormNotes(e.target.value)}
                placeholder={texts.phNotes}
                maxLength={500}
                className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition resize-none"
              />
            </div>

            {submitError && (
              <p className="text-red-500 text-xs flex items-center gap-1.5">
                <i className="ri-error-warning-line"></i>
                {texts.errorMsg}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3.5 bg-[#0F2340] hover:bg-sky-700 disabled:bg-slate-300 text-white text-sm font-bold rounded-lg transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  {texts.submitting}
                </>
              ) : (
                <>
                  <i className="ri-calendar-check-line"></i>
                  {texts.submitBtn}
                </>
              )}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

export default BookingCalendar;
